import { PlusIcon, PencilIcon, TrashIcon, BuildingOfficeIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import type { Client } from '@shared/types';

interface ClientListProps {
  clients: Client[];
  loading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  onCreateNew: () => void;
  onImportFromZohoBooks: () => void;
  onEdit: (client: Client) => void;
  onDelete: (id: string) => void;
}

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-gray-100 text-gray-700',
  prospect: 'bg-blue-100 text-blue-700',
};

export function ClientList({
  clients,
  loading,
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  onCreateNew,
  onImportFromZohoBooks,
  onEdit,
  onDelete,
}: ClientListProps) {
  return (
    <div className="bg-white shadow-sm rounded-lg">
      <div className="p-4 sm:p-6 border-b border-gray-200">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Clients</h2>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={onImportFromZohoBooks}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              title="Import a customer from Zoho Books"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
              Import from Zoho
            </button>
            <button
              onClick={onCreateNew}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              <PlusIcon className="w-5 h-5" />
              New Client
            </button>
          </div>
        </div>

        <input
          type="text"
          placeholder="Search clients..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 mb-3"
        />

        <select
          value={statusFilter}
          onChange={(e) => onStatusFilterChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="prospect">Prospect</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : clients.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <BuildingOfficeIcon className="w-12 h-12 mx-auto mb-2 text-gray-400" />
            No clients found
          </div>
        ) : (
          clients.map((client) => (
            <div
              key={client.id}
              className="p-4 hover:bg-gray-50 cursor-pointer"
              onClick={() => onEdit(client)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-gray-900">{client.name}</h3>
                    <span
                      className={`px-2 py-1 text-xs rounded ${
                        statusColors[client.status] || 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {client.status}
                    </span>
                  </div>
                  {(client.city || client.state) && (
                    <p className="text-sm text-gray-600 mt-1">
                      {client.city}{client.city && client.state && ', '}{client.state}
                    </p>
                  )}
                  {client.industry && (
                    <p className="text-xs text-gray-400 mt-1">
                      Industry: {client.industry}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(client);
                    }}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-md"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(client.id);
                    }}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

